import React, { useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import "../auth.css";
import { changePasswordApi, clearTokens } from "../services/authService";

function ChangePasswordPage({ onPasswordChanged, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePassword = (pwd) => {
    const issues = [];
    if (pwd.length < 12) issues.push("at least 12 characters");
    if (pwd.length > 128) issues.push("no more than 128 characters");
    if (!/[A-Z]/.test(pwd)) issues.push("an uppercase letter");
    if (!/[a-z]/.test(pwd)) issues.push("a lowercase letter");
    if (!/[0-9]/.test(pwd)) issues.push("a number");
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pwd)) issues.push("a special character");
    const uniqueChars = new Set(pwd).size;
    if (uniqueChars < 5) issues.push("at least 5 unique characters");
    return issues;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    const issues = validatePassword(newPassword);
    if (issues.length > 0) {
      setError(`Password must contain ${issues.join(", ")}.`);
      return;
    }

    if (newPassword === currentPassword) {
      setError("New password must be different from current password.");
      return;
    }

    setLoading(true);

    try {
      await changePasswordApi(currentPassword, newPassword);
      setSuccess(true);
      // After a brief pause, redirect to login
      setTimeout(() => {
        clearTokens();
        if (onPasswordChanged) onPasswordChanged();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <section className="login-left-panel" aria-hidden="true">
        <div className="login-brand">
          <span className="login-brand-arrow">&gt;</span>
          <span className="login-brand-text">Accenture</span>
        </div>
        <h1 className="login-title">ATC Gen AI Store</h1>
        <p className="login-subtitle">
          Your password must be changed before you can continue.
        </p>
      </section>

      <section className="login-right-panel">
        <div className="login-card">
          <h2 className="login-card-title">Change Password</h2>
          <p className="login-card-text">
            Create a strong password with 12–128 characters, including uppercase, lowercase, number, special character, and 5 unique characters.
          </p>

          {success ? (
            <div className="login-success">
              <p className="login-success-text">
                Password changed successfully! Redirecting to login…
              </p>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit}>
              <label htmlFor="currentPassword" className="login-label">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="login-input"
                placeholder="Enter current password"
                autoComplete="current-password"
                required
                disabled={loading}
              />

              <label htmlFor="newPassword" className="login-label">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="login-input"
                placeholder="Enter new password"
                autoComplete="new-password"
                required
                disabled={loading}
              />

              <label htmlFor="confirmPassword" className="login-label">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="login-input"
                placeholder="Confirm new password"
                autoComplete="new-password"
                required
                disabled={loading}
              />

              {error && (
                <p className="login-error">
                  <FiAlertCircle className="login-error-icon" />
                  <span>{error}</span>
                </p>
              )}

              <button
                type="submit"
                className="login-submit-btn"
                disabled={loading}
              >
                {loading ? "Updating…" : "Change Password"}
              </button>

              <button
                type="button"
                className="login-back-btn"
                onClick={() => {
                  clearTokens();
                  if (onLogout) onLogout();
                }}
                disabled={loading}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

export default ChangePasswordPage;
